// apps/client/src/pages/reports/GroupSnapshotPage.tsx
//
// Group Snapshot — configure and issue an investor-facing snapshot link.
//
// ACCESS: super_admin (or founder) AND `featureFlags.company_overview_share`.
// The nav entry carries the same gate and BOTH companyShare callables enforce it
// server-side. The nav gate alone is never the control: this mints a PUBLIC,
// unauthenticated URL over group-wide commission figures.
//
// NO FILE IS EVER PRODUCED. Sharing is an expiring, token-validated link hosted
// on VALUTTEN; the recipient prints to PDF from their own browser if they want
// one. That is the no-data-export invariant, not an implementation detail.
//
// Icons: `Share2` for the page. Vault / TrendingUp / PiggyBank / Handshake /
// Activity / HandCoins / Sparkles / SearchCheck are RESERVED for specific
// metrics (apps/client/CLAUDE.md) and must not be reused here.
//
// Sizing is REM-first via the Tailwind scale; no pixel hardcodes.

import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  Share2, X, Copy, Check, Loader2, ExternalLink,
  AlertTriangle, ChevronDown, SlidersHorizontal,
} from 'lucide-react';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';

type PeriodKind =
  | 'named_month' | 'calendar_ytd' | 'financial_ytd' | 'financial_year'
  | 'current_quarter' | 'last_quarter' | 'trailing_12m';

type SectionKeyName =
  | 'topline' | 'officeBreakdown' | 'loanBooks'
  | 'brokerConcentration' | 'lenderConcentration' | 'basisOfPreparation';

interface SectionDef { key: SectionKeyName; label: string; hint: string }

// Order mirrors the report itself so the list reads top-to-bottom like the page.
// `aboutValutten` is deliberately NOT here: VALUTTEN's own attribution and
// contact block on a VALUTTEN-hosted link is not the tenant's to switch off.
const SECTIONS: SectionDef[] = [
  { key: 'topline', label: 'Group position', hint: 'Book, trail, settled volume' },
  { key: 'officeBreakdown', label: 'Book & trail by office', hint: 'Per-office split' },
  { key: 'loanBooks', label: 'All loan books', hint: 'Every book, acquired included' },
  { key: 'brokerConcentration', label: 'Broker concentration', hint: 'Key-person risk' },
  { key: 'lenderConcentration', label: 'Lender concentration', hint: 'Lender spread' },
  { key: 'basisOfPreparation', label: 'Basis of preparation', hint: 'How figures are derived' },
];

const PERIOD_LABELS: Record<PeriodKind, string> = {
  named_month: 'Single month',
  financial_ytd: 'Financial year to date',
  financial_year: 'Last financial year',
  calendar_ytd: 'Calendar year to date',
  current_quarter: 'Current quarter',
  last_quarter: 'Last quarter',
  trailing_12m: 'Trailing 12 months',
};

function monthOptions(count = 18): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 1; i <= count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function monthLabel(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({
  checked, onChange, label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-150 ${
      checked ? 'bg-blue-600' : 'bg-gray-600'
    }`}
  >
    <span
      className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-150 ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

/**
 * Share modal. Mirrors the ShareModal in TrailBookValuationPage so every share
 * link in the app behaves the same way: click, a dialog opens, and the result -
 * loading, error, or the link itself - appears where the user is looking.
 *
 * The previous version rendered the outcome inline at the bottom of a long
 * settings page, well below the fold, so a failure looked like nothing had
 * happened at all.
 */
const ShareModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  shareUrl: string | null;
  expiresAt: string | null;
  error: string | null;
  needsAck: boolean;
  onIssueAnyway: () => void;
}> = ({ isOpen, onClose, isLoading, shareUrl, expiresAt, error, needsAck, onIssueAnyway }) => {
  const [copied, setCopied] = useState(false);
  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-white">Share Group Snapshot</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-300">Generating share link...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className={`p-4 rounded-lg mb-4 border ${
            needsAck
              ? 'bg-yellow-500/20 border-yellow-500/30'
              : 'bg-red-500/20 border-red-500/30'
          }`}>
            <div className="flex gap-3 items-start">
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                needsAck ? 'text-yellow-400' : 'text-red-400'
              }`} />
              <p className={`text-sm leading-relaxed ${needsAck ? 'text-yellow-200' : 'text-red-400'}`}>
                {error}
              </p>
            </div>
            {needsAck && (
              <button
                onClick={onIssueAnyway}
                className="mt-4 w-full px-4 py-2 rounded-lg text-sm font-semibold border border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/10"
              >
                Issue anyway and label it provisional
              </button>
            )}
          </div>
        )}

        {shareUrl && !isLoading && (
          <>
            <p className="text-gray-300 mb-4">
              Share this link to give read-only access to this snapshot. The link includes a
              snapshot of the data as it stands now and cannot be changed afterwards.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono"
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center gap-2 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-gray-500">
                {expiresAt
                  ? `This link expires ${new Date(expiresAt).toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}.`
                  : 'This link expires.'}
              </p>
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Preview
              </a>
            </div>
          </>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const GroupSnapshotPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<{ userData: any; companyData: any }>();

  const months = React.useMemo(() => monthOptions(), []);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [periodKind, setPeriodKind] = useState<PeriodKind>('named_month');
  const [namedMonth, setNamedMonth] = useState<string>(months[0]);
  const [commissionBasis, setCommissionBasis] = useState<'net' | 'gross' | 'both'>('net');
  const [brokerDetail, setBrokerDetail] = useState<'off' | 'anonymised' | 'named'>('anonymised');
  const [upfrontCommission, setUpfrontCommission] = useState(false);
  const [settlementVolume, setSettlementVolume] = useState(true);
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [sections, setSections] = useState<Record<string, boolean>>(
    Object.fromEntries(SECTIONS.map((s) => [s.key, true])),
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsAck, setNeedsAck] = useState(false);

  // Defence in depth. The nav hides this and both callables enforce it, but a
  // pasted URL must not render the configuration surface either.
  const allowed = (userData?.role === 'super_admin' || userData?.isFounder === true)
    && companyData?.featureFlags?.company_overview_share === true;

  if (!allowed) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-white mb-2">Not available</h2>
          <p className="text-gray-400 text-sm">Group Snapshot is not enabled for your account.</p>
        </div>
      </div>
    );
  }

  const createLink = async (acknowledgeIncompletePeriod = false) => {
    setModalOpen(true);
    setIsCreating(true);
    setError(null);
    setNeedsAck(false);
    setShareUrl(null);
    try {
      const call = httpsCallable(functions, 'generateCompanyShareToken');
      const res: any = await call({
        companyId: userData?.companyId,
        expiresInDays,
        acknowledgeIncompletePeriod,
        config: {
          period: periodKind === 'named_month'
            ? { kind: 'named_month', month: namedMonth }
            : { kind: periodKind },
          commissionBasis,
          metrics: { upfrontCommission, settlementVolume },
          breakdowns: { brokerDetail },
          sections,
        },
      });
      setShareUrl(res?.data?.shareUrl ?? null);
      setExpiresAt(res?.data?.expiresAt ?? null);
    } catch (e: any) {
      // The completeness gate is a deliberate refusal, not a failure: surface it
      // as a decision the user makes rather than an error they retry blindly.
      // Plain English - the raw server message names a request parameter, which
      // means nothing to the person reading it.
      if (e?.code === 'functions/failed-precondition') {
        setNeedsAck(true);
        setError(
          'Not all commission runs have been received for this period yet. '
          + 'About 90% of a month\'s trail arrives in the final run, so the figures '
          + 'would understate the group. You can wait for the remaining runs, or issue '
          + 'the link now clearly labelled provisional.',
        );
      } else {
        setError(e?.message ?? 'Could not create the share link.');
      }
    } finally {
      setIsCreating(false);
    }
  };


  const selectClass =
    'w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white '
    + 'focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60';
  const labelClass = 'block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2';

  const periodSummary = periodKind === 'named_month'
    ? monthLabel(namedMonth)
    : PERIOD_LABELS[periodKind];
  const basisSummary = commissionBasis === 'net' ? 'Net'
    : commissionBasis === 'gross' ? 'Gross' : 'Net and gross';
  const sectionsOn = SECTIONS.filter((s) => sections[s.key] !== false).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Share2}
        title="Group Snapshot"
        subtitle="Issue a read-only, expiring snapshot of the group for an external reader"
        badge="Super Admin"
        actions={(
          <button
            onClick={() => createLink(false)}
            disabled={isCreating}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Share2 className="w-3.5 h-3.5" />}
            <span>Create share link</span>
          </button>
        )}
      />

      {/* Settings — collapsible. The summary line keeps the chosen configuration
          visible once collapsed, so the page is not a wall of controls. */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => setSettingsOpen((o) => !o)}
          aria-expanded={settingsOpen}
          className="w-full p-6 flex items-start justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 bg-blue-500/20 rounded-lg flex-shrink-0">
              <SlidersHorizontal className="w-4 h-4 text-blue-400" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-lg text-white">Snapshot settings</h3>
              <p className="text-sm text-gray-400 mt-1">
                {periodSummary} &middot; {basisSummary} &middot; {sectionsOn} of {SECTIONS.length} sections
                {upfrontCommission ? ' · upfront shown' : ''}
              </p>
            </div>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 flex-shrink-0 mt-1 transition-transform duration-150 ${
              settingsOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {settingsOpen && (
          <div className="border-t border-gray-700">
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div>
                <label className={labelClass}>Period</label>
                <select className={selectClass} value={periodKind}
                  onChange={(e) => setPeriodKind(e.target.value as PeriodKind)}>
                  {(Object.keys(PERIOD_LABELS) as PeriodKind[]).map((k) => (
                    <option key={k} value={k}>{PERIOD_LABELS[k]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Month</label>
                <select
                  className={`${selectClass} ${periodKind !== 'named_month' ? 'opacity-40 cursor-not-allowed' : ''}`}
                  value={namedMonth}
                  disabled={periodKind !== 'named_month'}
                  onChange={(e) => setNamedMonth(e.target.value)}
                >
                  {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
              </div>

              <div>
                <label className={labelClass}>Commission basis</label>
                <select className={selectClass} value={commissionBasis}
                  onChange={(e) => setCommissionBasis(e.target.value as never)}>
                  <option value="net">Net</option>
                  <option value="gross">Gross</option>
                  <option value="both">Net and gross</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Broker detail</label>
                <select className={selectClass} value={brokerDetail}
                  onChange={(e) => setBrokerDetail(e.target.value as never)}>
                  <option value="anonymised">Anonymised</option>
                  <option value="named">Named</option>
                  <option value="off">Hidden</option>
                </select>
              </div>
            </div>

            <div className="px-6 pb-6">
              <label className={labelClass}>Include</label>
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={settlementVolume} className="accent-blue-600 w-4 h-4"
                    onChange={(e) => setSettlementVolume(e.target.checked)} />
                  Settlement volume
                  <span className="text-xs text-gray-500">loans written, not revenue</span>
                </label>
                <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={upfrontCommission} className="accent-blue-600 w-4 h-4"
                    onChange={(e) => setUpfrontCommission(e.target.checked)} />
                  Upfront commission
                  <span className="text-xs text-gray-500">revenue</span>
                </label>
              </div>
            </div>

            {/* Sections — one column. A two-up grid left an orphan row and read
                as unbalanced against the rest of the app. */}
            <div className="px-6 pb-6">
              <label className={labelClass}>Sections in the share link</label>
              <div className="rounded-lg border border-gray-700 divide-y divide-gray-700 overflow-hidden">
                {SECTIONS.map((s) => (
                  <div key={s.key}
                    className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-900/40">
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium">{s.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{s.hint}</p>
                    </div>
                    <Toggle
                      label={s.label}
                      checked={sections[s.key] !== false}
                      onChange={(v) => setSections((prev) => ({ ...prev, [s.key]: v }))}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                A section switched off is never queried and never written to the link, so its
                figures never leave the platform.
              </p>
            </div>

            <div className="px-6 pb-6 pt-6 border-t border-gray-700">
              <label className={labelClass}>Link expires after</label>
              <select className={`${selectClass} max-w-[10rem]`} value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <ShareModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        isLoading={isCreating}
        shareUrl={shareUrl}
        expiresAt={expiresAt}
        error={error}
        needsAck={needsAck}
        onIssueAnyway={() => createLink(true)}
      />
    </div>
  );
};

export default GroupSnapshotPage;
